/* Cart-value offers extend the existing Byloro offer catalog and cart lock. */
(function (root) {
  "use strict";
  const VERSION = "1.0.0";
  const PROPERTY = "Personalisatie";
  const ATTRIBUTE = "_byloro_offer_id";
  // DiscountOS needs unique automatic-discount titles and Gift with Purchase takes max 5 variants, so one offer can span several rules.
  const titlesOf = offer => (Array.isArray(offer.discountTitles) && offer.discountTitles.length ? offer.discountTitles : [offer.discountTitle]).filter(Boolean);
  const id = value => String(value || "").split("/").pop();
  const cents = value => Number.isSafeInteger(value) && value >= 0 ? value : null;
  const role = line => (line.properties || {})._byloro_offer_role || (line.properties || {})._byloro_campaign_line;
  const owned = (line, offer) => (line.properties || {})._byloro_offer_id === offer.id && role(line) === "gift";
  const giftLines = (cart, offer) => (cart.items || []).filter(line => owned(line, offer));
  const withinWindow = (offer, now = Date.now()) => {
    const start = Date.parse(offer.startsAt), end = Date.parse(offer.endsAt);
    return Number.isFinite(start) && Number.isFinite(end) && start < end && now >= start && now < end;
  };
  function subtotal(cart) {
    let total = 0;
    for (const line of cart.items || []) {
      if (role(line) === "gift" || line.gift_card) continue;
      const amount = cents(line.final_line_price);
      if (amount === null) return null;
      total += amount;
      if (!Number.isSafeInteger(total)) return null;
    }
    return total;
  }
  function competingOffer(cart, offer) {
    return (cart.items || []).some(line => {
      const other = (line.properties || {})._byloro_offer_id;
      return other && other !== offer.id;
    }) || Boolean(cart.attributes?._byloro_offer_id && cart.attributes._byloro_offer_id !== offer.id);
  }
  function hasOtherDiscount(cart, offer) {
    if ((cart.discount_codes || []).some(code => code.code && code.applicable !== false)) return true;
    if ((cart.cart_level_discount_applications || []).some(discount => Number(discount.total_allocated_amount) > 0)) return true;
    return (cart.items || []).some(line => (line.line_level_discount_allocations || []).some(allocation =>
      Number(allocation.amount) > 0 && (!owned(line, offer) || !titlesOf(offer).includes(allocation.discount_application?.title))));
  }
  function eligibility(cart, offer, context = {}) {
    if (offer.enabled !== true) return { eligible: false, reason: "disabled" };
    if (cart.currency !== offer.currency) return { eligible: false, reason: "currency" };
    if (!offer.countryCodes.includes(context.country)) return { eligible: false, reason: "country" };
    if (!withinWindow(offer, context.now)) return { eligible: false, reason: "period" };
    if (competingOffer(cart, offer)) return { eligible: false, reason: "other_offer" };
    if (hasOtherDiscount(cart, offer)) return { eligible: false, reason: "discount" };
    const amount = subtotal(cart);
    if (amount === null) return { eligible: false, reason: "amount_unknown" };
    return { eligible: amount >= offer.minimumAmount, reason: amount >= offer.minimumAmount ? "" : "threshold", amount };
  }
  function validateSelection(offer, variantId, symbols) {
    const variant = offer.variants.find(item => id(item.id) === id(variantId));
    if (!variant || !variant.available || !offer.allowedVariantIds.includes(id(variantId))) return { valid: false, reason: "variant" };
    if (!Array.isArray(symbols) || symbols.length !== 2 || symbols.some(value => !offer.characters.some(character => character.value === value))) {
      return { valid: false, reason: "personalization" };
    }
    return { valid: true, variant, personalization: symbols.join(" + ") };
  }
  function verifyGift(cart, offer, context) {
    const lines = giftLines(cart, offer);
    if (!eligibility(cart, offer, context).eligible || lines.length !== 1) return false;
    const line = lines[0], properties = line.properties || {};
    const selected = validateSelection(offer, line.id, [properties["Teken 1"], properties["Teken 2"]]);
    const allocations = (line.line_level_discount_allocations || []).filter(item => Number(item.amount) > 0);
    const original = cents(line.original_line_price);
    return selected.valid && properties[PROPERTY] === selected.personalization &&
      properties._byloro_offer === offer.id && properties._byloro_offer_version === offer.version &&
      Boolean(properties._byloro_flow_id) && Number(line.quantity) === 1 && original > 0 &&
      original === Number(line.original_price) && Number(line.final_line_price) === 0 && Number(line.final_price) === 0 &&
      allocations.length === 1 && Number(allocations[0].amount) === original &&
      titlesOf(offer).includes(allocations[0].discount_application?.title) &&
      cart.attributes?.[ATTRIBUTE] === offer.id &&
      Number(cart.total_discount) === original &&
      Number(cart.total_price) === (cart.items || []).reduce((sum, item) => sum + Number(item.final_line_price), 0);
  }
  function propertiesFor(offer, selection, flowId) {
    return { [PROPERTY]: selection.personalization, "Teken 1": selection.symbols[0], "Teken 2": selection.symbols[1],
      _byloro_offer: offer.id, _byloro_offer_id: offer.id, _byloro_offer_version: offer.version,
      _byloro_offer_role: "gift", _byloro_campaign_line: "gift", _byloro_flow_id: flowId };
  }
  function environmentAllowed(offer, win) {
    if (!offer.enabled || !offer.discountId || !titlesOf(offer).length) return false;
    const theme = win.Shopify?.theme || {};
    if (offer.mode === "preview") return String(theme.id) === String(offer.previewThemeId) && String(theme.role).toLowerCase() === "unpublished";
    return offer.mode === "production" && offer.liveApproved === true && String(theme.role).toLowerCase() === "main";
  }
  function selectOffer(offer, base, url, persisted, cart) {
    const requested = new URL(url).searchParams.get("offer");
    // An explicit different/unknown campaign must never silently become this offer.
    if (requested) return requested === offer.id ? offer.id : null;
    if (persisted && persisted !== offer.id) return null;
    if (competingOffer(cart, offer)) return null;
    return giftLines(cart, offer).length || persisted === offer.id || offer.defaultOffer ? offer.id : null;
  }

  // The Prestige cart PAGE has no cart:refresh listener (only the drawer does); the existing offer flow reloads it instead.
  const reloadsOnRefresh = win => win?.themeVariables?.settings?.pageType === "cart";
  // Drawer markup has no .cart-recap, so fall back to the drawer footer form (slot="footer").
  const noticeHostFor = doc => doc.querySelector("main .cart-recap") || doc.querySelector("cart-drawer .cart-recap") || doc.querySelector("cart-drawer .cart-drawer__footer");

  // The "gift removed" notice must survive the cart-page reload that follows a removal. The flag is written before the reload and
  // consumed exactly once by the NEXT page load (at construction); the page that sets it must never consume it itself.
  function removalFlag(read, write, key) {
    let pending = read(key) === "true";
    if (pending) write(key, "");
    return { mark: () => write(key, "true"), take: () => { const was = pending; pending = false; return was; } };
  }

  function mount(doc, offer) {
    const win = doc.defaultView, base = win.BYLORO_OFFER_FLOW;
    if (!base || doc.querySelector("[data-byloro-cart-offer-mounted]")) return null;
    const canPromote = environmentAllowed(offer, win);
    const theme = win.Shopify?.theme || {};
    const canClean = offer.hasOwnedGift && ((offer.mode === "production" && String(theme.role).toLowerCase() === "main") ||
      (offer.mode === "preview" && String(theme.id) === String(offer.previewThemeId) && String(theme.role).toLowerCase() === "unpublished"));
    if (!canPromote && !canClean) return null;
    if (!canPromote) offer = { ...offer, enabled: false };
    if (!offer.id || !offer.countryCodes?.length || cents(offer.minimumAmount) === null || (!offer.variants?.length && !canClean)) return null;
    // Register in the existing catalog, with a qualifier shape understood by legacy readers.
    offer.qualifying = { type: "cart_subtotal", productId: "", variantIds: [], minimumQuantity: 1 };
    base.catalog[offer.id] = offer;
    const localeRoot = win.Shopify?.routes?.root || "/";
    const storageKey = "byloro-offer-context";
    const dismissedKey = "byloro-offer-dismissed:" + offer.id;
    const readStorage = key => { try { return win.sessionStorage.getItem(key); } catch (_) { return null; } };
    const store = (key, value) => { try { win.sessionStorage.setItem(key, value); } catch (_) {} };
    const context = () => ({ country: offer.country, now: Date.now() });
    const requestedOffer = new URL(win.location.href).searchParams.get("offer");
    if (requestedOffer) store(storageKey, requestedOffer);
    let latest = null, pending = null, busy = false, unsafe = false, bypassCheckout = false, returnFocus = null, hadEligible = false;
    const shell = doc.createElement("section");
    shell.dataset.byloroCartOfferMounted = offer.id;
    shell.className = "byloro-cart-offer";
    shell.innerHTML = '<div class="byloro-cart-offer__notice" hidden><p data-offer-message role="status" aria-live="polite"></p><button type="button" data-offer-open>Personaliseer je gratis armband</button></div><dialog aria-labelledby="byloro-cart-offer-title"><form data-offer-form><button type="button" data-offer-close aria-label="Sluiten" class="byloro-cart-offer__close">×</button><h2 id="byloro-cart-offer-title">Jouw gratis Initial Bracelet</h2><p>Bij een aankoop vanaf €39,99 krijg je één armband met twee eigen initialen of tekens cadeau.</p><img data-offer-image alt="Initial Bracelet" width="220" height="220"><label>Kleur<select name="color" required></select></label><label>Maat<select name="size" required></select></label><label>Eerste initiaal of teken<select name="first" required></select></label><label>Tweede initiaal of teken<select name="second" required></select></label><a data-symbol-reference target="_blank" rel="noopener">Bekijk de initialen en symbolen</a><p data-offer-error role="alert"></p><button type="submit" data-offer-submit>Voeg mijn gratis armband toe</button><p>Basisuitvoering, zonder luxe cadeauverpakking. Eén cadeau per bestelling. Niet te combineren met andere kortingen of cadeauacties.</p><button type="button" data-offer-close>Verder winkelen</button></form></dialog>';
    doc.body.append(shell);
    const dialog = shell.querySelector("dialog"), form = shell.querySelector("form"), notice = shell.querySelector(".byloro-cart-offer__notice");
    const message = shell.querySelector("[data-offer-message]"), openButton = shell.querySelector("[data-offer-open]");
    const error = shell.querySelector("[data-offer-error]"), submit = shell.querySelector("[data-offer-submit]");
    notice.classList.add("byloro-cart-offer");
    // Prestige replaces the footer asynchronously after cart:refresh. Keep the original
    // notice (and its click listener) attached even when that happens after paint().
    const attachNotice = () => {
      const host = noticeHostFor(doc) || shell;
      if (notice.parentElement !== host) host.prepend(notice);
    };
    const noticeObserver = new win.MutationObserver(attachNotice);
    noticeObserver.observe(doc.body, { childList: true, subtree: true });
    const field = name => form.elements.namedItem(name);
    const option = (value, label, disabled = false) => { const node = doc.createElement("option"); node.value = value; node.textContent = label; node.disabled = disabled; return node; };
    for (const name of ["color", "size", "first", "second"]) field(name).append(option("", "Maak een keuze"));
    for (const color of [...new Set(offer.variants.map(variant => variant.color))]) field("color").append(option(color, color));
    for (const character of offer.characters) for (const name of ["first", "second"]) field(name).append(option(character.value, character.label));
    if (offer.imageUrl) shell.querySelector("[data-offer-image]").src = offer.imageUrl;
    else shell.querySelector("[data-offer-image]").hidden = true;
    shell.querySelector("[data-symbol-reference]").href = offer.symbolReferenceUrl;
    const sizes = () => {
      const previous = field("size").value;
      field("size").replaceChildren(option("", "Maak een keuze"));
      for (const variant of offer.variants.filter(item => item.color === field("color").value)) field("size").append(option(variant.size, variant.size, !variant.available));
      if ([...field("size").options].some(item => item.value === previous && !item.disabled)) field("size").value = previous;
    };
    field("color").addEventListener("change", sizes);
    const showError = text => { error.textContent = text; };
    const close = () => { if (busy) return; dialog.close(); store(dismissedKey, "true"); returnFocus?.focus?.(); };
    const open = () => {
      if (!latest || !eligibility(latest, offer, context()).eligible || busy) return;
      const gift = giftLines(latest, offer)[0];
      if (gift) {
        const variant = offer.variants.find(item => id(item.id) === id(gift.id));
        if (variant) { field("color").value = variant.color; sizes(); field("size").value = variant.size; }
        field("first").value = gift.properties["Teken 1"] || ""; field("second").value = gift.properties["Teken 2"] || "";
      }
      showError(""); returnFocus = doc.activeElement; dialog.showModal(); field("color").focus();
    };
    openButton.addEventListener("click", open);
    shell.querySelectorAll("[data-offer-close]").forEach(button => button.addEventListener("click", close));
    dialog.addEventListener("cancel", event => { event.preventDefault(); close(); });
    async function request(path, body) {
      const response = await win.fetch(localeRoot + path, { method: body ? "POST" : "GET", credentials: "same-origin",
        headers: { Accept: "application/json", ...(body ? { "Content-Type": "application/json" } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {}), signal: win.AbortSignal?.timeout(12000) });
      if (!response.ok) throw new Error("cart_request");
      return response.json();
    }
    const getCart = () => request("cart.js");
    const removeLine = line => request("cart/change.js", { id: line.key, quantity: 0 });
    const refresh = () => {
      try { win.localStorage.setItem("byloro-offer-cart-updated", String(Date.now())); } catch (_) {}
      if (reloadsOnRefresh(win)) win.location.reload(); else doc.dispatchEvent(new win.CustomEvent("cart:refresh", { bubbles: true }));
    };
    const removedFlag = removalFlag(readStorage, store, "byloro-offer-removed:" + offer.id);
    function paint(cart, removed) {
      latest = cart;
      if (removedFlag.take()) removed = true;
      const active = selectOffer(offer, base, win.location.href, readStorage(storageKey), cart);
      const qualification = eligibility(cart, offer, context());
      const allowed = active && qualification.eligible;
      const conflict = active && ["other_offer", "discount"].includes(qualification.reason) && subtotal(cart) >= offer.minimumAmount;
      const confirmed = giftLines(cart, offer).length === 1 && verifyGift(cart, offer, context());
      unsafe = giftLines(cart, offer).length > 0 && !confirmed;
      notice.hidden = !allowed && !removed && !conflict;
      openButton.hidden = !allowed;
      if (removed) message.textContent = "Je gratis armband is verwijderd omdat je bestelling niet meer aan de actievoorwaarden voldoet.";
      else if (conflict) message.textContent = "Deze armbandactie is niet te combineren met de andere korting of cadeauactie in je winkelwagen. Je bestaande keuze is behouden.";
      else if (confirmed) message.textContent = "Je armband is gratis. Personalisatie: " + giftLines(cart, offer)[0].properties[PROPERTY];
      else message.textContent = "Je mag een gratis Initial Bracelet kiezen bij je bestelling.";
      openButton.textContent = confirmed ? "Personalisatie wijzigen" : "Personaliseer je gratis armband";
      if (allowed) {
        store(storageKey, offer.id);
        if (!hadEligible && !confirmed && readStorage(dismissedKey) !== "true") open();
      } else if (dialog.open && !busy) dialog.close();
      hadEligible = Boolean(allowed);
      attachNotice();
      return { cart, removed, confirmed, eligible: Boolean(allowed) };
    }
    async function reconcile() {
      let cart = await getCart(), removed = false;
      const gifts = giftLines(cart, offer);
      // Remove only this offer's gifts. Paid bracelets and other offers remain untouched.
      if (gifts.length && (!eligibility(cart, offer, context()).eligible || !verifyGift(cart, offer, context()))) {
        for (const line of gifts) { await removeLine(line); removed = true; }
        if ((cart.attributes || {})[ATTRIBUTE] === offer.id) await request("cart/update.js", { attributes: { [ATTRIBUTE]: "" } });
        cart = await getCart();
        if (reloadsOnRefresh(win)) removedFlag.mark();
        refresh();
      }
      return paint(cart, removed);
    }
    function check() {
      if (pending) return pending;
      pending = base.withCartLock(doc, reconcile).catch(problem => {
        unsafe = unsafe || Boolean(latest && giftLines(latest, offer).length);
        notice.hidden = false; openButton.hidden = true;
        message.textContent = "We kunnen je cadeau nu niet controleren. Probeer opnieuw voordat je afrekent.";
        throw problem;
      }).finally(() => { pending = null; });
      return pending;
    }
    form.addEventListener("submit", async event => {
      event.preventDefault();
      if (busy || !form.reportValidity()) return;
      if (unsafe) {
        try { await check(); } catch (_) { showError("Je winkelwagen kon niet worden gecontroleerd. Probeer het opnieuw."); return; }
      }
      const variant = offer.variants.find(item => item.color === field("color").value && item.size === field("size").value);
      const symbols = [field("first").value, field("second").value];
      const selection = validateSelection(offer, variant?.id, symbols);
      if (!selection.valid) { showError("Kies een beschikbare kleur en maat en vul beide tekens in."); return; }
      busy = true; submit.disabled = true; form.setAttribute("aria-busy", "true"); showError("");
      let changed = false;
      try {
        await base.withCartLock(doc, async () => {
          const before = await getCart();
          if (!eligibility(before, offer, context()).eligible || !selectOffer(offer, base, win.location.href, readStorage(storageKey), before)) throw new Error("not_eligible");
          const previous = giftLines(before, offer);
          if (previous.length > 1) throw new Error("duplicate_gift");
          const flowId = previous[0]?.properties?._byloro_flow_id || win.crypto.randomUUID();
          const properties = propertiesFor(offer, { ...selection, symbols }, flowId);
          unsafe = true;
          // Server-side rule is a Gift with Purchase gated on this cart attribute (the line property cannot be a GWP condition).
          if ((before.attributes || {})[ATTRIBUTE] !== offer.id) await request("cart/update.js", { attributes: { [ATTRIBUTE]: offer.id } });
          if (previous[0] && id(previous[0].id) === id(variant.id)) {
            await request("cart/change.js", { id: previous[0].key, quantity: 1, properties });
          } else {
            if (previous[0]) await removeLine(previous[0]);
            await request("cart/add.js", { items: [{ id: id(variant.id), quantity: 1, properties }] });
          }
          changed = true;
          const after = await getCart();
          const existing = { ...before, items: before.items.filter(line => !owned(line, offer)) };
          if (!base.sameExistingCartLines(existing, after) || !verifyGift(after, offer, context())) throw new Error("not_free");
          paint(after, false);
        });
        busy = false; dialog.close(); store(dismissedKey, "true"); returnFocus?.focus?.(); refresh();
      } catch (_) {
        // Reconcile uncertain submissions before any retry; never repeat cart/add automatically.
        await base.withCartLock(doc, reconcile).catch(() => {});
        showError("Je cadeau kon niet veilig worden bevestigd. Controleer je winkelwagen voordat je verdergaat en probeer opnieuw.");
        if (changed) refresh();
      } finally { busy = false; submit.disabled = false; form.removeAttribute("aria-busy"); }
    });
    const quietly = () => { if (!busy) check().catch(() => {}); };
    doc.addEventListener("cart:change", quietly);
    doc.addEventListener("cart:refresh", quietly);
    win.addEventListener("pageshow", quietly);
    win.addEventListener("storage", event => { if (event.key === "byloro-offer-cart-updated") quietly(); });
    // Observe completed storefront cart writes without altering their responses or reading bodies.
    const originalFetch = win.fetch;
    win.fetch = function (input, init) {
      const response = originalFetch.apply(this, arguments);
      let url;
      try { url = new URL(typeof input === "string" ? input : input.url, win.location.href); } catch (_) { return response; }
      if (url.origin === win.location.origin && /\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname)) {
        response.then(result => { if (result.ok) win.setTimeout(quietly, 0); }, () => {});
      }
      return response;
    };
    function beforeCheckout(event) {
      const target = event.target.closest?.('[name="checkout"],a[href*="/checkout"]');
      const submitted = event.type === "submit" && event.submitter?.name === "checkout";
      if (bypassCheckout || (!target && !submitted)) return;
      if (!unsafe && (!latest || (!giftLines(latest, offer).length && !busy))) return;
      event.preventDefault(); event.stopImmediatePropagation();
      if (busy) return;
      check().then(result => {
        if (result.removed) return;
        bypassCheckout = true;
        try { if (event.type === "submit") event.target.requestSubmit(event.submitter); else if (target.tagName === "A") win.location.assign(target.href); else target.click(); }
        finally { bypassCheckout = false; }
      }).catch(() => {});
    }
    doc.addEventListener("click", beforeCheckout, true); doc.addEventListener("submit", beforeCheckout, true);
    quietly();
    return { check, open, get latest() { return latest; } };
  }
  const api = { VERSION, subtotal, eligibility, validateSelection, verifyGift, propertiesFor, environmentAllowed, selectOffer, giftLines, reloadsOnRefresh, noticeHostFor, removalFlag, mount };
  if (typeof module !== "undefined" && module.exports) module.exports = api;
  root.BYLORO_CART_OFFER = api;
  if (root.document) {
    const start = () => {
      const node = root.document.querySelector("script[data-byloro-cart-offer-config]");
      if (!node) return;
      try { mount(root.document, JSON.parse(node.textContent)); } catch (_) { /* Invalid configuration never activates an offer. */ }
    };
    if (root.document.readyState === "loading") root.document.addEventListener("DOMContentLoaded", start, { once: true }); else start();
  }
})(typeof window !== "undefined" ? window : globalThis);
